import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Download, Share2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function ShareCardDialog({ open, onOpenChange, message, profile }) {
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  if (!message) return null;

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        backgroundColor: "#F5F2EB",
        cacheBust: true,
      });
      const link = document.createElement("a");
      link.download = `hammerl-story-${profile?.name || "reiter"}-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Story-Karte heruntergeladen. Ungerino approves.");
    } catch (e) {
      toast.error("Export fehlgeschlagen — versuch's nochmal.");
    } finally {
      setDownloading(false);
    }
  };

  const handleNativeShare = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: "#F5F2EB", cacheBust: true });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "hammerl-story.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Hobby Horsing Hammerl", text: "Schau dir meine Galopp-Story an!" });
      } else {
        handleDownload();
      }
    } catch (e) {
      if (e.name !== "AbortError") toast.error("Teilen nicht möglich.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none border-2 border-hammerl-ink shadow-brutal-lg bg-hammerl-paper p-0 overflow-hidden" data-testid="share-card-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 bg-hammerl-card border-b-2 border-hammerl-ink">
          <DialogTitle className="font-bold text-xl tracking-tight flex items-center gap-2">
            <Share2 className="w-5 h-5" /> Story-Highlight teilen
          </DialogTitle>
          <DialogDescription className="font-mono text-xs uppercase tracking-widest text-hammerl-pencil">
            Ungerino sagt: Das wird viral.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {/* The card to be exported */}
          <div
            ref={cardRef}
            className="bg-hammerl-paper border-4 border-hammerl-ink shadow-brutal-lg p-8 relative"
            style={{ width: "560px", maxWidth: "100%", margin: "0 auto" }}
            data-testid="share-card-preview"
          >
            <div className="flex items-center justify-between border-b-2 border-hammerl-ink pb-3 mb-5">
              <div className="flex items-center gap-2">
                <img src="/brand/hammerl-logo.jpg" alt="Hobby Horsing Hammerl Logo" className="w-9 h-9 object-cover border border-hammerl-ink" />
                <div>
                  <div className="font-bold text-xs tracking-tight">HOBBY HORSING HAMMERL</div>
                  <div className="text-[10px] font-mono text-hammerl-pencil uppercase tracking-widest">Galopp-Chronik</div>
                </div>
              </div>
              <div className="text-right font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil">
                <div>Reiter: {profile?.name}</div>
                <div>LVL {profile?.level} · {profile?.xp} XP</div>
              </div>
            </div>

            <div className="font-mono text-[10px] uppercase tracking-widest text-hammerl-pencil mb-2">Game Master Protokoll</div>
            <div className="font-handwriting text-2xl text-hammerl-blue leading-relaxed whitespace-pre-wrap">
              "{message.content}"
            </div>

            <div className="mt-6 pt-4 border-t-2 border-hammerl-ink flex items-center justify-between">
              <div className="stamp text-xs">SYNERGIE</div>
              <div className="text-right">
                <div className="font-handwriting text-base text-hammerl-ink">— Hammerl GmbH &amp; Co. KG</div>
                <div className="font-mono text-[10px] text-hammerl-pencil tracking-widest">hobby-horsing-hammerl</div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-hammerl-card border-t-2 border-hammerl-ink flex flex-wrap gap-2 justify-end">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-none hover:bg-hammerl-sidebar"
            data-testid="share-card-close-btn"
          >
            <X className="w-4 h-4 mr-1" /> Schließen
          </Button>
          {typeof navigator !== "undefined" && navigator.canShare && (
            <Button
              onClick={handleNativeShare}
              className="rounded-none bg-hammerl-ink hover:bg-hammerl-corpblue text-white font-bold shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
              data-testid="share-card-native-btn"
            >
              <Share2 className="w-4 h-4 mr-1" /> Teilen
            </Button>
          )}
          <Button
            onClick={handleDownload}
            disabled={downloading}
            className="rounded-none bg-hammerl-corpblue hover:bg-hammerl-ink text-white font-bold shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
            data-testid="share-card-download-btn"
          >
            <Download className="w-4 h-4 mr-1" /> {downloading ? "Lade…" : "PNG laden"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
